import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useCachedQuery, useCachedMutations } from '@/hooks/useCachedQuery'
import { useApi } from '@/hooks/useApi'
import { showToast } from '@/components/layout/Toast'
import Modal from '@/components/layout/Modal'
import type { Cotizacion, CotizacionItemsEstandar } from '@/lib/types'
import { formatCurrency, formatDate } from '@/lib/utils'
import { IconEye, IconEyeOff, IconEdit, IconTrash, IconFile, IconUser, IconCheckCircle, IconShare } from '@/components/icons/Icons'
import { PageHeader } from '@/components/ui'

const estadoBadge: Record<string, string> = {
  borrador: 'badge-borrador', enviada: 'badge-enviada', aceptada: 'badge-aceptada', rechazada: 'badge-rechazada',
}

/**
 * ¿Es una cotización de marca blanca? Tiene ítems que salieron de nuestro
 * catálogo con su `costo_partner`, así que hay un partner del otro lado a
 * quien mostrársela —o no—. Una cotización directa de 1Conecta no lo es.
 */
function esDeMarcaBlanca(c: Cotizacion): boolean {
  if (!Array.isArray(c.items)) return false
  return (c.items as CotizacionItemsEstandar[]).some(it => Number(it?.costo_partner) > 0)
}

const tipoBadge: Record<string, { label: string; icon: React.ReactNode; cls: string }> = {
  estandar: { label: 'Estándar', icon: <IconFile size={12} />, cls: 'cot-tipo--estandar' },
  equipo_dedicado: { label: 'Equipo', icon: <IconUser size={12} />, cls: 'cot-tipo--equipo' },
  tareas: { label: 'Tareas', icon: <IconCheckCircle size={12} />, cls: 'cot-tipo--tareas' },
}

export default function Cotizaciones() {
  const { data: items = [], isLoading: loading } = useCachedQuery<Cotizacion>('cotizaciones')
  const { removeMut } = useCachedMutations('cotizaciones')
  const api = useApi()
  const qc = useQueryClient()
  const [del, setDel] = useState<Cotizacion | null>(null)
  const [search, setSearch] = useState('')
  const [cambiando, setCambiando] = useState<number | null>(null)

  /*
    Compartir el borrador con el partner, o retirarlo.

    Una cotización de marca blanca que armamos nosotros nace fuera de su
    panel: entre que se crea y que está lista para que él le ponga su precio
    hay un rato de edición, y en ese rato mostrarle un documento a medio
    hacer es peor que no mostrarle nada. Este botón es el momento en que se
    decide que ya está.

    Va por ruta propia (`/visibilidad-partner`) y no por el PUT general: ese
    lo usa también el partner, y esto es justo lo que él no puede escribir.
  */
  const cambiarVisibilidad = async (c: Cotizacion) => {
    const visible = c.visible_partner === false
    setCambiando(c.id)
    try {
      await api.put(`/api/cotizaciones/${c.id}/visibilidad-partner`, { visible })
      await qc.invalidateQueries({ queryKey: ['cotizaciones'] })
      showToast(visible ? 'El partner ya la ve en su panel' : 'Retirada del panel del partner', 'success')
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'No se pudo cambiar', 'error')
    } finally { setCambiando(null) }
  }

  const doDelete = async () => { if (!del) return; try { await removeMut.mutateAsync(del.id); showToast('Eliminada', 'success'); setDel(null) } catch { showToast('Error', 'error') } }

  const filtered = items.filter(c =>
    [c.numero, c.cliente_nombre, c.proyecto_nombre].some(v => v?.toLowerCase().includes(search.toLowerCase()))
  )

  if (loading) return <div className="page-enter" style={{ padding: '2rem' }}><PageHeader breadcrumbs={[{ label: 'CRM' }, { label: 'Cotizaciones' }]} /><div style={{ color: 'var(--text-secondary)', marginTop: '2rem', textAlign: 'center' }}><div className="auth-spinner" style={{ margin: '0 auto 12px' }} />Cargando...</div></div>

  return (
    <div className="page-enter">
      <PageHeader
        breadcrumbs={[{ label: 'CRM' }, { label: 'Cotizaciones' }]}
        meta={`${items.length} cotización${items.length !== 1 ? 'es' : ''}`}
        actions={
          <>
            <Link to="/tareas-cot/nueva" className="btn btn-secondary">Propuesta por Tareas</Link>
            <Link to="/equipo/nueva" className="btn btn-secondary">Equipo Dedicado</Link>
            <Link to="/cotizaciones/nueva" className="btn btn-primary">Nueva Cotización</Link>
          </>
        }
      />

      {items.length > 3 && (
        <div style={{ marginBottom: 16 }}><input className="form-input" placeholder="Buscar por N°, cliente o proyecto..." value={search} onChange={e => setSearch(e.target.value)} style={{ maxWidth: 400 }} /></div>
      )}

      {items.length === 0 ? (
        <div className="empty-state"><h3>Sin cotizaciones</h3><p>Crea tu primera cotización.</p><Link to="/cotizaciones/nueva" className="btn btn-primary">Crear Cotización</Link></div>
      ) : (
        <div className="table-container"><table><thead><tr><th>N°</th><th>Tipo</th><th>Cliente</th><th>Proyecto</th><th>Fecha</th><th>Total</th><th>Estado</th><th style={{ width: 150 }}>Acciones</th></tr></thead>
          <tbody>{filtered.map(c => (
            <tr key={c.id}>
              <td style={{ fontWeight: 700, fontFamily: 'monospace' }}>{c.numero}</td>
              <td><span className={`cot-tipo-badge ${tipoBadge[c.tipo_cotizacion]?.cls || ''}`}>{tipoBadge[c.tipo_cotizacion]?.icon} {tipoBadge[c.tipo_cotizacion]?.label || c.tipo_cotizacion}</span></td>
              <td>{c.cliente_nombre || '—'}</td>
              <td style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.proyecto_nombre || '—'}</td>
              <td>{formatDate(c.fecha_emision)}</td>
              <td style={{ fontWeight: 700 }}>{formatCurrency(c.total, c.moneda)}</td>
              <td>
                <span className={`badge ${estadoBadge[c.estado] || ''}`}>{c.estado}</span>
                {/* Sólo se dice cuando está oculta: lo normal es que el
                    partner la vea, y un badge en cada fila sería ruido. */}
                {c.visible_partner === false && <span className="badge badge-borrador" title="El partner todavía no la ve">Interna</span>}
              </td>
              <td>
                <div style={{ display: 'flex', gap: 6 }}>
                  <Link to={`/${c.tipo_cotizacion === 'equipo_dedicado' ? 'equipo' : c.tipo_cotizacion === 'tareas' ? 'tareas-cot' : 'cotizaciones'}/${c.id}`} className="btn btn-sm btn-secondary" title="Ver"><IconEye size={14} /></Link>
                  <Link to={`/${c.tipo_cotizacion === 'equipo_dedicado' ? 'equipo' : c.tipo_cotizacion === 'tareas' ? 'tareas-cot' : 'cotizaciones'}/${c.id}/editar`} className="btn btn-sm btn-secondary" title="Editar"><IconEdit size={14} /></Link>
                  {/* El botón aparece sólo en las de marca blanca: una
                      cotización nuestra, directa, no tiene partner a quien
                      mostrársela. */}
                  {esDeMarcaBlanca(c) && (
                    <button
                      className={`btn btn-sm ${c.visible_partner === false ? 'btn-primary' : 'btn-secondary'}`}
                      onClick={() => cambiarVisibilidad(c)}
                      disabled={cambiando === c.id}
                      title={c.visible_partner === false ? 'Compartir con el partner' : 'Retirar del panel del partner'}
                    >
                      {c.visible_partner === false ? <IconShare size={14} /> : <IconEyeOff size={14} />}
                    </button>
                  )}
                  <button className="btn btn-sm btn-danger" onClick={() => setDel(c)} title="Eliminar"><IconTrash size={14} /></button>
                </div>
              </td>
            </tr>
          ))}</tbody></table></div>
      )}

      <Modal isOpen={!!del} onClose={() => setDel(null)} title="Eliminar Cotización">
        <p>¿Eliminar cotización <strong>N° {del?.numero}</strong>?</p>
        <div className="modal-actions"><button className="btn btn-secondary" onClick={() => setDel(null)}>Cancelar</button><button className="btn btn-danger" onClick={doDelete}>Eliminar</button></div>
      </Modal>
    </div>
  )
}
